import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FastField } from 'formik';

import { Block as LabelBlock } from '../Label';
import { Select as BoneyardSelect, VisuallyHidden } from '../Boneyard';
import getNamespacedFieldName from './getNamespacedFieldName';
import getFormPartAutomationHook from './getFormPartAutomationHook';
import useFastFieldShouldRenderFn from './useFastFieldShouldRenderFn';
import FieldError from './FieldError';
import getFormikFieldProps from './getFormikFieldProps';

const propTypes = {
  //to be true when there should be no visible label;
  ariaLabelOnly: PropTypes.bool,
  // Taken into consideration only when ariaLabelOnly is set to true.
  // If set to true, it will visually maintain the empty height of the label
  // on top of the field.
  labelTransparent: PropTypes.bool,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  namespace: PropTypes.string,
  automationHook: PropTypes.string,
  forceErrorStyles: PropTypes.bool,
  ariaDescribedBy: PropTypes.string,
  fieldLevelValidation: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  showOwnErrorMessage: PropTypes.bool,
  resetOnUnmount: PropTypes.bool,
  menuPortalTarget: PropTypes.object,
  autoComplete: PropTypes.string,
  ariaLabel: PropTypes.string,
  // ... plus other Boneyard Select fields
};

const VisuallyTransparent = styled.div`
  opacity: 0;
`;

export const SelectField = ({
  namespace,
  label,
  ariaLabelOnly,
  labelTransparent,
  isRequired,
  name,
  automationHook,
  forceErrorStyles,
  ariaDescribedBy,
  fieldLevelValidation,
  onChange,
  onBlur,
  onMenuOpen,
  onMenuClose,
  resetOnUnmount,
  showOwnErrorMessage = true,
  menuPortalTarget,
  autoComplete = 'off',
  ariaLabel,
  ...props
}) => {
  const fieldName = getNamespacedFieldName(name, namespace);
  const fieldAutomationHook = getFormPartAutomationHook(
    automationHook,
    name,
    namespace
  );

  const HidingComponent = labelTransparent
    ? VisuallyTransparent
    : VisuallyHidden;

  // Replicates the checks in FastFields, and adds the "options" prop
  // to the check.
  // https://github.com/jaredpalmer/formik/blob/master/packages/formik/src/FastField.tsx#L75-L93
  const shouldUpdate = useFastFieldShouldRenderFn(['options', 'validate']);

  return (
    <>
      {label && !ariaLabelOnly ? (
        <LabelBlock
          htmlFor={fieldName}
          isRequired={isRequired}
          automationHook={fieldAutomationHook}
        >
          {label}
        </LabelBlock>
      ) : (
        <HidingComponent>
          <div>
            <LabelBlock
              htmlFor={fieldName}
              isRequired={isRequired}
              automationHook={fieldAutomationHook}
            >
              {label}
            </LabelBlock>
          </div>
        </HidingComponent>
      )}
      <FastField
        name={fieldName}
        shouldUpdate={shouldUpdate}
        options={props.options}
        validate={fieldLevelValidation}
      >
        {({ field, form, meta }) => {
          const fieldWithCustomHandlers = getFormikFieldProps({
            fieldName,
            field,
            meta,
            forceErrorStyles,
            onChange,
            onBlur,
          });

          return (
            <>
              <BoneyardSelect
                {...props}
                // Automatically injected by FieldHandler, but
                // added to appease PropTypes.
                menuPortalTarget={menuPortalTarget}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                name={fieldName}
                id={fieldName}
                automationHook={fieldAutomationHook}
                autoComplete={autoComplete}
                ariaDescribedBy={ariaDescribedBy || `${fieldName}_error`}
                {...fieldWithCustomHandlers}
                ariaLabel={ariaLabel}
              />
              {showOwnErrorMessage ? (
                <FieldError fieldName={fieldName} label={label} />
              ) : null}
            </>
          );
        }}
      </FastField>
    </>
  );
};
SelectField.propTypes = propTypes;
SelectField.displayName = 'SelectField';

export default React.memo(SelectField);
